import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import Header from "../../common/Header";
import SideBar from "../../common/SideBar";
import SkeletonTicketList from "../../../loaders/SkeletonTicketList";
import NumberInput from "../../library/NumberInput";
import TextInput from "../../library/TextInput";
import Checkbox from "../../library/Checkbox";
import PositiveButton from "../../library/PositiveButton";
import ButtonGroup from "../../library/ButtonGroup";
import CommonButton from "../../common/Button/Button";
import { SubmitFirstAidBoxUsage } from "../../../services/FirstAidBoxService";
import { invalidateFirstAidBoxQueries, useFirstAidBoxUsageForm } from "../../../query/hooks/QueryHooksFirstAidBox";
import { getOhsErrorMessage } from "./ohsUtils";
import {
    CategoryHeading,
    CheckItemName,
    ConfirmationPanel,
    DesktopOnlyNav,
    FormGrid,
    MutedText,
    SectionCard,
    SectionHeader,
    SectionTitle,
    TagResultMessage,
    TagStepStack,
    UsageItemRow
} from "./Ohs.styles";

/**
 * "Log items used" - the screen a box's QR code opens. Any logged-in AMI user who takes something
 * from the box records what they used and the tamper tag they swapped.
 * Route: /campusAdministration/ohs-use/:boxCode
 */
function BoxUsage() {
    const history = useHistory();
    const { boxCode } = useParams();
    const { data: usageForm, isLoading } = useFirstAidBoxUsageForm(boxCode);
    const [quantitiesUsed, setQuantitiesUsed] = useState({});
    const [tagSerials, setTagSerials] = useState({ tagSerialFound: "", newTagSerial: "" });
    const [isTagMissing, setIsTagMissing] = useState(false);
    const [formErrors, setFormErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [recordedUsage, setRecordedUsage] = useState(null);

    useEffect(() => {
        if (!usageForm) {
            return;
        }
        setQuantitiesUsed(Object.fromEntries(usageForm.items.map((usageItem) => [usageItem.id, 0])));
    }, [usageForm]);

    const setTagSerial = (fieldName, value) => {
        setTagSerials((currentSerials) => ({ ...currentSerials, [fieldName]: value }));
        setFormErrors((currentErrors) => ({ ...currentErrors, [fieldName]: undefined }));
    };

    const handleSubmit = async () => {
        const hasUsedSomething = Object.values(quantitiesUsed).some((quantityUsed) => Number(quantityUsed) > 0);
        const requiredErrors = {
            items: hasUsedSomething ? undefined : "Enter how many of at least one item you used.",
            tagSerialFound: isTagMissing || tagSerials.tagSerialFound.trim() ? undefined : "Enter the serial on the tag you removed, or select No Tag on the Box",
            newTagSerial: tagSerials.newTagSerial.trim() ? undefined : "Enter the serial on the new tag you fitted",
        };
        if (Object.values(requiredErrors).some(Boolean)) {
            setFormErrors(requiredErrors);
            return;
        }

        setIsSubmitting(true);
        try {
            const { data } = await SubmitFirstAidBoxUsage({
                boxId: usageForm.box.id,
                items: usageForm.items.map((usageItem) => ({
                    boxItemId: usageItem.id,
                    quantityUsed: Number(quantitiesUsed[usageItem.id]) || 0,
                })),
                isTagMissing,
                tagSerialFound: isTagMissing ? undefined : tagSerials.tagSerialFound.trim(),
                newTagSerial: tagSerials.newTagSerial.trim(),
            });
            await invalidateFirstAidBoxQueries();
            setRecordedUsage(data.data);
        } catch (error) {
            console.log("BoxUsage.js->handleSubmit->Error", error);
            Swal.fire("Error", getOhsErrorMessage(error, "The items used could not be saved."), "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderContent = () => {
        if (isLoading) {
            return <SkeletonTicketList />;
        }
        if (!usageForm) {
            return <MutedText>This first aid box could not be found. It may have been deleted.</MutedText>;
        }
        if (recordedUsage) {
            return (
                <ConfirmationPanel>
                    <i className="fal fa-check-circle"></i>
                    <SectionTitle>Thank you - your usage was recorded</SectionTitle>
                    <MutedText>Box {usageForm.box.box_code} has been updated and restocking will be arranged if needed.</MutedText>
                    {recordedUsage.isTagMismatch && (
                        <TagResultMessage $condition="damaged">
                            {isTagMissing
                                ? "There was no tag on the box - an OHS Representative has been flagged to review it."
                                : "The tag you removed doesn't match the one on record - an OHS Representative has been flagged to review it."}
                        </TagResultMessage>
                    )}
                </ConfirmationPanel>
            );
        }

        return (
            <SectionCard>
                <SectionHeader>
                    <div>
                        <SectionTitle>Log Items Used: {usageForm.box.box_code}</SectionTitle>
                        <MutedText>
                            {usageForm.box.campus_name} · {usageForm.box.building_name} · {usageForm.box.area_location}
                        </MutedText>
                    </div>
                </SectionHeader>

                <CategoryHeading>Tamper Tag</CategoryHeading>
                <FormGrid>
                    <TextInput
                        id="ohsUsageTagFound"
                        label="Serial on the tag you removed"
                        required
                        maxLength={60}
                        value={tagSerials.tagSerialFound}
                        onChange={(event) => setTagSerial("tagSerialFound", event.target.value)}
                        error={Boolean(formErrors.tagSerialFound)}
                        errorText={formErrors.tagSerialFound}
                        disabled={isTagMissing}
                    />
                    <TextInput
                        id="ohsUsageNewTag"
                        label="Serial on the new tag you fitted"
                        required
                        maxLength={60}
                        value={tagSerials.newTagSerial}
                        onChange={(event) => setTagSerial("newTagSerial", event.target.value)}
                        error={Boolean(formErrors.newTagSerial)}
                        errorText={formErrors.newTagSerial}
                    />
                </FormGrid>
                <TagStepStack>
                    <Checkbox
                        id="ohsUsageTagMissing"
                        label="No Tag on the Box"
                        checked={isTagMissing}
                        onChange={(event) => {
                            setIsTagMissing(event.target.checked);
                            setTagSerial("tagSerialFound", "");
                        }}
                    />
                </TagStepStack>

                <CategoryHeading>Items you used</CategoryHeading>
                {formErrors.items && <MutedText>{formErrors.items}</MutedText>}
                {usageForm.items.map((usageItem) => (
                    <UsageItemRow key={usageItem.id}>
                        <CheckItemName>
                            {usageItem.item_name}
                            <div><MutedText>{usageItem.current_quantity} {usageItem.unit} in the box</MutedText></div>
                        </CheckItemName>
                        <NumberInput
                            aria-label={`${usageItem.item_name} quantity used`}
                            size="md"
                            min={0}
                            max={usageItem.current_quantity}
                            value={quantitiesUsed[usageItem.id] ?? 0}
                            onChange={(quantityUsed) => {
                                setQuantitiesUsed((currentQuantities) => ({ ...currentQuantities, [usageItem.id]: quantityUsed }));
                                setFormErrors((currentErrors) => ({ ...currentErrors, items: undefined }));
                            }}
                        />
                    </UsageItemRow>
                ))}

                <ButtonGroup className="mt-4">
                    <PositiveButton onClick={handleSubmit} disabled={isSubmitting} />
                    <CommonButton type="button" variant="danger" onClick={() => history.push("/")} iconLeft={<i className="fal fa-times"></i>}>
                        Cancel
                    </CommonButton>
                </ButtonGroup>
            </SectionCard>
        );
    };

    return (
        <>
            <Header />
            <div className="content-wrapper d-flex flex-wrap new-standard-box">
                <DesktopOnlyNav>
                    <SideBar />
                </DesktopOnlyNav>
                <div className="sidebar-right">
                    <div className="sub-menu-content-block">
                        <div className="sub-menu-right-block">
                            <div className="wrap-box-right">
                                <div className="my-tickets-heading-blk flex-direction mb-2">
                                    <div className="d-flex align-items-center">
                                        <div className="name-w-head d-flex align-items-center">
                                            <h4 className="text-left-align landing-heading heading_color_dynamic">
                                                First Aid <span>Box</span>
                                            </h4>
                                        </div>
                                    </div>
                                </div>
                                {renderContent()}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default BoxUsage;
